import type { ReactNode } from 'react';
import { AdminStatsInfoTip } from './AdminStatsInfoTip.js';

export const CHART_COLORS = [
  '#36e0ff',
  '#c77dff',
  '#63e6a6',
  '#ffb454',
  '#ff6b8a',
  '#7c9cff',
  '#f5e663',
  '#64d8cb',
];

export interface ChartDatum {
  label: string;
  value: number;
  color?: string;
  description?: string;
  href?: string;
}

export function DonutChart({
  data,
  center,
  ariaLabel,
}: {
  data: ChartDatum[];
  center: ReactNode;
  ariaLabel: string;
}) {
  const visible = data.filter((datum) => datum.value > 0);
  const total = visible.reduce((sum, datum) => sum + datum.value, 0);
  let offset = 0;
  return (
    <div className="astats-donut-layout">
      <div className="astats-donut" role="img" aria-label={ariaLabel}>
        <svg viewBox="0 0 42 42" aria-hidden="true">
          <circle className="astats-donut__track" cx="21" cy="21" r="15.9155" />
          {visible.map((datum, index) => {
            const share = total === 0 ? 0 : (datum.value / total) * 100;
            const segment = (
              <circle
                key={datum.label}
                className="astats-donut__segment"
                cx="21"
                cy="21"
                r="15.9155"
                stroke={datum.color ?? CHART_COLORS[index % CHART_COLORS.length]}
                strokeDasharray={`${share} ${100 - share}`}
                strokeDashoffset={-offset}
              >
                <title>{datum.description ? `${datum.label}: ${datum.description}` : datum.label}</title>
              </circle>
            );
            offset += share;
            return segment;
          })}
        </svg>
        <div className="astats-donut__center">{center}</div>
      </div>
      <div className="astats-legend">
        {visible.length === 0 && <span className="astats-muted">No data in this range</span>}
        {visible.map((datum, index) => (
          <div className="astats-legend__row" key={datum.label}>
            <span
              className="astats-legend__swatch"
              style={{ background: datum.color ?? CHART_COLORS[index % CHART_COLORS.length] }}
            />
            <span className="astats-legend__label-wrap">
              {datum.href ? (
                <a className="astats-legend__label" href={datum.href} target="_blank" rel="noreferrer" title={datum.label}>
                  {datum.label}
                </a>
              ) : (
                <span className="astats-legend__label" title={datum.label}>{datum.label}</span>
              )}
              {datum.description ? <AdminStatsInfoTip text={datum.description} /> : null}
            </span>
            <strong>{datum.value.toLocaleString('en-US', { maximumFractionDigits: 2 })}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

export function WindowBarChart({
  data,
  formatValue,
  ariaLabel,
  legendLabel,
}: {
  data: Array<{ label: string; value: number | null }>;
  formatValue: (value: number) => string;
  ariaLabel: string;
  legendLabel: string;
}) {
  const peak = Math.max(...data.map((datum) => datum.value ?? 0), 0);
  const scaleMax = Math.max(peak, 1);
  const sampled = data.length > 42 ? data.filter((_, index) => index % Math.ceil(data.length / 42) === 0) : data;
  const values = data.flatMap((datum) => datum.value == null ? [] : [datum.value]);
  const total = values.reduce((sum, value) => sum + value, 0);
  const latest = values.at(-1);
  return (
    <div className="astats-series" role="img" aria-label={ariaLabel}>
      <div className="astats-series__legend">
        <span><i />{legendLabel}</span>
        <span>Total <strong>{formatValue(total)}</strong></span>
        <span>Peak <strong>{formatValue(peak)}</strong></span>
        <span>Latest <strong>{latest == null ? '—' : formatValue(latest)}</strong></span>
      </div>
      <div className="astats-series__plot">
        <div className="astats-series__y"><span>{formatValue(peak)}</span><span>0</span></div>
        <div className="astats-bars">
          {sampled.map((datum, index) => (
            <div
              className="astats-bars__column"
              key={`${datum.label}-${index}`}
              title={`${datum.label}: ${datum.value == null ? 'unavailable' : formatValue(datum.value)}`}
            >
              <div
                className={`astats-bars__bar ${datum.value == null ? 'astats-bars__bar--missing' : ''}`}
                style={{ height: `${datum.value == null ? 4 : Math.max(4, (datum.value / scaleMax) * 100)}%` }}
              />
            </div>
          ))}
        </div>
      </div>
      <div className="astats-series__x">
        <span>{data[0]?.label ?? '—'}</span>
        {data.length > 2 ? <span>{data[Math.floor(data.length / 2)]?.label}</span> : null}
        <span>{data.at(-1)?.label ?? '—'}</span>
      </div>
    </div>
  );
}

export function LineChart({
  data,
  color,
  ariaLabel,
  legendLabel,
  formatValue,
}: {
  data: Array<{ label: string; value: number | null }>;
  color: string;
  ariaLabel: string;
  legendLabel: string;
  formatValue: (value: number) => string;
}) {
  const values = data.map((datum) => datum.value);
  const numbers = values.filter((value): value is number => value != null);
  if (numbers.length < 2) return <div className="astats-chart-empty">Not enough windows yet</div>;
  const min = Math.min(...numbers);
  const max = Math.max(...numbers);
  const span = max - min || 1;
  const width = 300;
  const height = 76;
  const step = width / Math.max(values.length - 1, 1);
  const coords = values.map((value, index) =>
    value == null ? null : ([index * step, height - 5 - ((value - min) / span) * (height - 10)] as const),
  );
  const path = coords
    .map((coord, index) =>
      coord == null
        ? ''
        : `${index === 0 || coords[index - 1] == null ? 'M' : 'L'}${coord[0].toFixed(1)},${coord[1].toFixed(1)}`,
    )
    .join(' ');
  const total = numbers.reduce((sum, value) => sum + value, 0);
  return (
    <div className="astats-series" role="img" aria-label={ariaLabel}>
      <div className="astats-series__legend">
        <span><i style={{ background: color }} />{legendLabel}</span>
        <span>Total <strong>{formatValue(total)}</strong></span>
        <span>High <strong>{formatValue(max)}</strong></span>
        <span>Latest <strong>{formatValue(numbers.at(-1) ?? 0)}</strong></span>
      </div>
      <div className="astats-series__plot">
        <div className="astats-series__y"><span>{formatValue(max)}</span><span>{formatValue(min)}</span></div>
        <svg className="astats-line" viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none" aria-hidden="true">
          <path d={path} fill="none" stroke={color} strokeWidth="2" vectorEffect="non-scaling-stroke" />
          {coords.map((coord, index) => coord ? (
            <circle key={index} cx={coord[0]} cy={coord[1]} r="2" fill={color}>
              <title>{data[index]?.label}: {formatValue(values[index] ?? 0)}</title>
            </circle>
          ) : null)}
        </svg>
      </div>
      <div className="astats-series__x"><span>{data[0]?.label}</span><span>{data[Math.floor(data.length / 2)]?.label}</span><span>{data.at(-1)?.label}</span></div>
    </div>
  );
}
